import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RoleBadge } from "@/components/members/RoleBadge";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow, format, isToday, isYesterday } from "date-fns";
import { 
  Activity, 
  UserPlus, 
  CheckCircle, 
  Play, 
  Ban, 
  UserMinus,
  MessageSquare,
  FileText,
  PenTool,
  CheckCheck,
  ListChecks,
  Link2,
  Layers,
  AlertCircle
} from "lucide-react";
import { MemberRole } from "@/lib/permissions";

interface ActivityEvent {
  id: string;
  type: 'comment' | 'system' | 'document' | 'signature' | 'subtask' | 'dependency' | 'phase' | 'assignment' | 'status';
  timestamp: string;
  user_id?: string;
  user_name: string;
  user_role?: MemberRole;
  action: string;
  details?: any;
  comment_text?: string;
  mentioned_user_ids?: string[];
  edited_at?: string;
}

interface TaskActivityFeedProps {
  taskId: string;
  unaId: string;
}

export function TaskActivityFeed({ taskId, unaId }: TaskActivityFeedProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'comments'>('all');
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (taskId) {
      fetchActivities();
      subscribeToActivities();
    }
  }, [taskId]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const allActivities: ActivityEvent[] = [];

      // 1. Fetch comments
      const { data: comments } = await supabase
        .from('task_comments')
        .select(`
          id,
          comment,
          created_at,
          edited_at,
          mentioned_user_ids,
          user_id
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (comments) {
        for (const c of comments) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', c.user_id)
            .single();

          allActivities.push({
            id: c.id,
            type: 'comment',
            timestamp: c.created_at,
            user_id: c.user_id,
            user_name: profile?.full_name || 'Unknown',
            comment_text: c.comment,
            mentioned_user_ids: (c.mentioned_user_ids as string[]) || [],
            edited_at: c.edited_at,
            action: 'commented',
            details: {}
          });
        }
      }

      // 2. Fetch task activity feed (system events)
      const { data: taskActivities } = await supabase
        .from('task_activity_feed')
        .select('*')
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (taskActivities) {
        for (const act of taskActivities) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', act.actor_id)
            .single();

          allActivities.push({
            id: act.id,
            type: 'system',
            timestamp: act.created_at,
            user_id: act.actor_id,
            user_name: profile?.full_name || 'System',
            action: act.activity_type,
            details: act.metadata || {}
          });
        }
      }

      // 3. Fetch documents
      const { data: documents } = await supabase
        .from('generated_documents')
        .select('id, document_name, status, generated_at, finalized_at, finalized_by, created_by')
        .eq('task_id', taskId)
        .order('generated_at', { ascending: false });

      if (documents) {
        for (const doc of documents) {
          // Document created
          const { data: creatorProfile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', doc.created_by)
            .maybeSingle();

          allActivities.push({
            id: `doc-created-${doc.id}`,
            type: 'document',
            timestamp: doc.generated_at,
            user_id: doc.created_by || undefined,
            user_name: creatorProfile?.full_name || 'System',
            action: 'document_created',
            details: { document_name: doc.document_name, status: doc.status }
          });

          // Document finalized
          if (doc.finalized_at && doc.finalized_by) {
            const { data: finalizerProfile } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('id', doc.finalized_by)
              .maybeSingle();

            allActivities.push({
              id: `doc-finalized-${doc.id}`,
              type: 'document',
              timestamp: doc.finalized_at,
              user_id: doc.finalized_by,
              user_name: finalizerProfile?.full_name || 'System',
              action: 'document_finalized',
              details: { document_name: doc.document_name }
            });
          }
        }
      }

      // 4. Fetch signature requests and signers
      const { data: sigRequests } = await supabase
        .from('signature_requests')
        .select(`
          id,
          created_at,
          created_by,
          status,
          completed_at,
          document:generated_documents(document_name)
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (sigRequests) {
        for (const req of sigRequests) {
          const { data: requestorProfile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', req.created_by)
            .maybeSingle();

          allActivities.push({
            id: `sig-req-${req.id}`,
            type: 'signature',
            timestamp: req.created_at,
            user_id: req.created_by,
            user_name: requestorProfile?.full_name || 'System',
            action: 'signature_requested',
            details: { document_name: (req.document as any)?.document_name }
          });

          // Fetch signers for this request
          const { data: signers } = await supabase
            .from('document_signers')
            .select('*')
            .eq('signature_request_id', req.id)
            .not('signed_at', 'is', null);

          if (signers) {
            for (const signer of signers) {
              allActivities.push({
                id: `sig-signed-${signer.id}`,
                type: 'signature',
                timestamp: signer.signed_at!,
                user_name: signer.signer_name,
                action: 'document_signed',
                details: { 
                  document_name: (req.document as any)?.document_name,
                  signer_role: signer.signer_role
                }
              });
            }
          }

          // Signature request completed
          if (req.completed_at) {
            allActivities.push({
              id: `sig-complete-${req.id}`,
              type: 'signature',
              timestamp: req.completed_at,
              user_name: 'System',
              action: 'signatures_completed',
              details: { document_name: (req.document as any)?.document_name }
            });
          }
        }
      }

      // 5. Fetch subtasks
      const { data: subtasks } = await supabase
        .from('task_subtasks')
        .select('*')
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (subtasks) {
        for (const subtask of subtasks) {
          const { data: creatorProfile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', subtask.created_by)
            .maybeSingle();

          allActivities.push({
            id: `subtask-created-${subtask.id}`,
            type: 'subtask',
            timestamp: subtask.created_at,
            user_id: subtask.created_by,
            user_name: creatorProfile?.full_name || 'System',
            action: 'subtask_created',
            details: { title: subtask.title }
          });

          if (subtask.completed_at) {
            const { data: completerProfile } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('id', subtask.created_by)
              .maybeSingle();

            allActivities.push({
              id: `subtask-completed-${subtask.id}`,
              type: 'subtask',
              timestamp: subtask.completed_at,
              user_id: subtask.created_by,
              user_name: completerProfile?.full_name || 'System',
              action: 'subtask_completed',
              details: { title: subtask.title }
            });
          }
        }
      }

      // 6. Fetch dependencies
      const { data: dependencies } = await supabase
        .from('task_dependencies')
        .select(`
          *,
          depends_on_task:custom_tasks!depends_on_task_id(task_title)
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: false });

      if (dependencies) {
        for (const dep of dependencies) {
          const { data: creatorProfile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', dep.created_by)
            .maybeSingle();

          allActivities.push({
            id: `dep-${dep.id}`,
            type: 'dependency',
            timestamp: dep.created_at,
            user_id: dep.created_by,
            user_name: creatorProfile?.full_name || 'System',
            action: 'dependency_added',
            details: { 
              dependency_type: dep.dependency_type,
              task_title: (dep.depends_on_task as any)?.task_title
            }
          });
        }
      }

      // 7. Fetch assignment changes
      const { data: assignments } = await supabase
        .from('task_assignments')
        .select('*')
        .eq('task_id', taskId)
        .order('assigned_at', { ascending: false });

      if (assignments) {
        for (const assignment of assignments) {
          const { data: assignerProfile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', assignment.assigned_by)
            .maybeSingle();

          const { data: memberInfo } = await supabase
            .from('una_members')
            .select('full_name')
            .eq('id', assignment.una_member_id)
            .maybeSingle();

          allActivities.push({
            id: `assignment-${assignment.id}`,
            type: 'assignment',
            timestamp: assignment.assigned_at,
            user_id: assignment.assigned_by,
            user_name: assignerProfile?.full_name || 'System',
            action: 'member_assigned',
            details: { member_name: memberInfo?.full_name }
          });

          if (assignment.completed_at) {
            allActivities.push({
              id: `assignment-complete-${assignment.id}`,
              type: 'assignment',
              timestamp: assignment.completed_at,
              user_id: assignment.una_member_id,
              user_name: memberInfo?.full_name || 'Member',
              action: 'assignment_completed',
              details: {}
            });
          }
        }
      }

      // Sort all activities by timestamp
      allActivities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setActivities(allActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToActivities = () => {
    const channel = supabase
      .channel(`task-${taskId}-comprehensive-activity`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_comments', filter: `task_id=eq.${taskId}` }, fetchActivities)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_activity_feed', filter: `task_id=eq.${taskId}` }, fetchActivities)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'generated_documents', filter: `task_id=eq.${taskId}` }, fetchActivities)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_subtasks', filter: `task_id=eq.${taskId}` }, fetchActivities)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_dependencies', filter: `task_id=eq.${taskId}` }, fetchActivities)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_assignments', filter: `task_id=eq.${taskId}` }, fetchActivities)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    const { error } = await supabase.from("task_comments").insert({
      task_id: taskId,
      user_id: user.id,
      una_id: unaId,
      comment: newComment.trim(),
    });

    if (error) {
      console.error("Error adding comment:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add comment",
      });
    } else {
      setNewComment("");
      toast({
        title: "Success",
        description: "Comment added successfully",
      });
    }
    setSubmitting(false);
  };

  const getDateSeparator = (timestamp: string) => {
    const date = new Date(timestamp);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'MMMM d, yyyy');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getActivityIcon = (event: ActivityEvent) => {
    switch (event.action) {
      case 'commented':
        return <MessageSquare className="h-4 w-4" />;
      case 'document_created':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'document_finalized':
        return <CheckCheck className="h-4 w-4 text-green-600" />;
      case 'signature_requested':
        return <PenTool className="h-4 w-4 text-purple-500" />;
      case 'document_signed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'signatures_completed':
        return <CheckCheck className="h-4 w-4 text-green-600" />;
      case 'subtask_created':
        return <ListChecks className="h-4 w-4 text-blue-500" />;
      case 'subtask_completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'dependency_added':
        return <Link2 className="h-4 w-4 text-orange-500" />;
      case 'member_assigned':
        return <UserPlus className="h-4 w-4 text-blue-500" />;
      case 'assignment_completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'task_started':
        return <Play className="h-4 w-4 text-green-500" />;
      case 'task_completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'task_blocked':
        return <Ban className="h-4 w-4 text-red-500" />;
      case 'assignee_removed':
        return <UserMinus className="h-4 w-4 text-red-400" />;
      case 'phase_created':
        return <Layers className="h-4 w-4 text-blue-500" />;
      case 'phase_started':
        return <Play className="h-4 w-4 text-green-500" />;
      case 'phase_completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'phase_blocked':
        return <Ban className="h-4 w-4 text-red-500" />;
      case 'phase_requirement_updated':
        return <ListChecks className="h-4 w-4 text-blue-400" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getActivityDescription = (event: ActivityEvent) => {
    switch (event.action) {
      case 'commented':
        return 'commented';
      case 'document_created':
        return `created document "${event.details.document_name}"`;
      case 'document_finalized':
        return `finalized document "${event.details.document_name}"`;
      case 'signature_requested':
        return `requested signatures for "${event.details.document_name}"`;
      case 'document_signed':
        return `signed "${event.details.document_name}" as ${event.details.signer_role}`;
      case 'signatures_completed':
        return `all signatures collected for "${event.details.document_name}"`;
      case 'subtask_created':
        return `created subtask "${event.details.title}"`;
      case 'subtask_completed':
        return `completed subtask "${event.details.title}"`;
      case 'dependency_added':
        return `added ${event.details.dependency_type.replace('_', ' ')} dependency: "${event.details.task_title}"`;
      case 'member_assigned':
        return `assigned ${event.details.member_name} to this task`;
      case 'assignment_completed':
        return 'completed their assignment';
      case 'task_started':
        return 'started working on this task';
      case 'task_completed':
        return 'completed this task';
      case 'task_blocked':
        return 'reported a blocking issue';
      case 'assignee_removed':
        return `removed ${event.details.member_name} from this task`;
      case 'phase_created':
        return `created Phase ${event.details.phase_number}: ${event.details.phase_name}`;
      case 'phase_started':
        return `started Phase ${event.details.phase_number}: ${event.details.phase_name}`;
      case 'phase_completed':
        return `completed Phase ${event.details.phase_number}: ${event.details.phase_name}`;
      case 'phase_blocked':
        return `blocked Phase ${event.details.phase_number}: ${event.details.phase_name}`;
      case 'phase_requirement_updated':
        return `updated requirements for Phase ${event.details.phase_number}: ${event.details.phase_name}`;
      default:
        return event.action.replace(/_/g, ' ');
    }
  };

  const renderActivityEvent = (event: ActivityEvent) => {
    if (event.type === 'comment') {
      return (
        <div className="flex gap-3 p-3 rounded-lg bg-background border">
          <Avatar className="h-8 w-8 mt-1">
            <AvatarFallback className="text-xs">
              {getInitials(event.user_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">{event.user_name}</span>
              {event.edited_at && (
                <Badge variant="secondary" className="text-xs">
                  Edited
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
              </span>
            </div>
            <p className="text-sm whitespace-pre-wrap break-words">
              {event.comment_text}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex gap-3 p-3 rounded-lg bg-muted/30">
        <div className="p-2 rounded-full bg-muted h-fit mt-1">
          {getActivityIcon(event)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{event.user_name}</span>
            {event.user_role && (
              <RoleBadge role={event.user_role} size="small" abbreviated />
            )}
            <span className="text-sm text-muted-foreground">{getActivityDescription(event)}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {formatDistanceToNow(new Date(event.timestamp), { addSuffix: true })}
          </p>
        </div>
      </div>
    );
  };

  const filteredActivities = filter === 'comments' 
    ? activities.filter(a => a.type === 'comment')
    : activities;

  // Group by date
  const groupedActivities: { [key: string]: ActivityEvent[] } = {};
  filteredActivities.forEach(activity => {
    const dateKey = getDateSeparator(activity.timestamp);
    if (!groupedActivities[dateKey]) {
      groupedActivities[dateKey] = [];
    }
    groupedActivities[dateKey].push(activity);
  });

  return (
    <div className="space-y-4">
      {/* Comment Input */}
      <div className="space-y-2 pb-4 border-b">
        <Textarea
          placeholder="Add a comment to this task..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          rows={3}
          className="resize-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              handleAddComment();
            }
          }}
        />
        <div className="flex justify-between items-center">
          <span className="text-xs text-muted-foreground">
            Press ⌘/Ctrl + Enter to post
          </span>
          <Button 
            onClick={handleAddComment}
            disabled={!newComment.trim() || submitting}
            size="sm"
          >
            Post Comment
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Activity Feed</h3>
          <Badge variant="secondary">{filteredActivities.length}</Badge>
        </div>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as 'all' | 'comments')}>
          <TabsList>
            <TabsTrigger value="all">All Activity</TabsTrigger>
            <TabsTrigger value="comments">Comments Only</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading activity...</div>
        ) : Object.keys(groupedActivities).length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {filter === 'comments' ? 'No comments yet. Start the conversation!' : 'No activity yet'}
          </div>
        ) : (
          Object.entries(groupedActivities).map(([date, events]) => (
            <div key={date}>
              <div className="sticky top-0 bg-background z-10 py-2">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {date}
                </h4>
              </div>
              <div className="space-y-3">
                {events.map((event) => (
                  <div key={event.id}>
                    {renderActivityEvent(event)}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
